<template>
	<div>
		<div v-if="report.loading && !loadingFacade && facade">
			<chart-legend show-current></chart-legend>
			<card tag="section" layout="metric">
				<metric-box-facade metric="totalBillings" :results="facade.metrics" :samples="facade.samples" :includeSegment="false">
					Total Billings
				</metric-box-facade>
				<metric-box-facade metric="assignmentsTaken" type="number" :results="facade.metrics" :samples="facade.samples" :includeSegment="false">
					Assignments Taken
				</metric-box-facade>
				<metric-box-facade metric="teamingRevenue" :results="facade.metrics" :samples="facade.samples" :includeSegment="false">
					Teaming Revenue
				</metric-box-facade>
				<metric-box-facade metric="teamingTaken" type="number" :results="facade.metrics" :samples="facade.samples" :includeSegment="false">
					Teaming Taken
				</metric-box-facade>
				<metric-box-facade metric="filledJobValue" :results="facade.metrics" :samples="facade.samples" :includeSegment="false">
					Filled Job Value
				</metric-box-facade>
				<metric-box-facade metric="timeToFill" type="number" :results="facade.metrics" :samples="facade.samples" :includeSegment="false">
					Time to Fill
				</metric-box-facade>
			</card>
		</div>
		<loading v-if="report.loading && (loadingFacade || (!loadingFacade && !facade))"></loading>
		<div v-else-if="!report.loading">
			<o-field v-if="companyId > 0" label="Show for">
				<o-select v-model="include">
					<option :value="null">This company only</option>
					<option value="subsidiaries">This company and all its subsidiaries</option>
					<option value="all">This company's entire family tree</option>
				</o-select>
			</o-field>
			<div v-if="report.results.length == 0">
				<h3 class="py-6">No recent finacial activity</h3>
			</div>
			<div v-else>
				<h4 class="has-text-centered blue mb-4">
					{{ ToQuarter(report.filter.date.startDate) }} -
					{{ ToQuarter(report.filter.date.endDate) }} (last 4 complete quarters)
				</h4>
				<chart-legend show-current></chart-legend>
				<card tag="section" layout="metric">
					<metric-box metric="totalBillings" :results="metrics" :report="report" :includeSegment="false">
						Total Billings
					</metric-box>
					<metric-box metric="assignmentsTaken" type="number" :results="metrics" :report="report" :includeSegment="false">
						Assignments Taken
					</metric-box>
					<metric-box metric="teamingRevenue" :results="metrics" :report="report" :includeSegment="false">
						Teaming Revenue
					</metric-box>
					<metric-box metric="teamingTaken" type="number" :results="metrics" :report="report" :includeSegment="false">
						Teaming Taken
					</metric-box>
					<metric-box metric="filledJobValue" :results="metrics" :report="report" :includeSegment="false">
						Filled Job Value
					</metric-box>
					<metric-box metric="timeToFill" type="number" :results="metrics" :report="report" :includeSegment="false">
						Time to Fill
					</metric-box>
				</card>
			</div>
		</div>
	</div>
</template>
<script>
	import { ref, inject, onMounted, computed, watch } from "vue";
	import { GenericReport } from "@/assets/types";
	import metricBox from "@/components/webComponents/metricBox.vue";
	import metricBoxFacade from "@/components/webComponents/dashboardFacade/metricBoxFacade.vue";
	import { legend } from "@/components/charts";
	import { kpi_datasets } from "@/components/webComponents/charts/kpiOutput.js";

	export default {
		components: {
			metricBox,
			metricBoxFacade,
			chartLegend: legend
		},
		props: {
			companyId: {
				type: Number,
				default: null
			},
			filters: {
				type: Object,
				default: () => {
					return {};
				}
			}
		},
		emit: ["dates"],
		setup(props, context) {
			const $financial = inject("$financial");
			const $json = inject("$json");
			const $http = inject("$http");
			const $error = inject("$error");
			const moment = inject("$moment");

			const hasRan = ref(false);
			const include = ref("subsidiaries");

			const report = ref(new GenericReport("Financial"));

			const facade = ref(null);
			const loadingFacade = ref(false);

			const defaultDate = {
				selection: "1Y",
				startDate: moment().utc().add(-1, "years").startOf("day").toDate(),
				endDate: moment().utc().endOf("day").toDate()
			};

			const metrics = computed(() => {
				const m = {
					totalBillings: $financial.getTotalBillings(report.value.results),
					assignmentsTaken: $financial.getAssignmentsTaken(report.value.results),
					teamingRevenue: $financial.getTeamingRevenue(report.value.results),
					teamingTaken: $financial.getTeamingTaken(report.value.results),
					filledJobValue: $financial.getFilledJobValue(report.value.results),
					timeToFill: $financial.getTimeToFill(report.value.results)
				};
				BuildFacade({ metrics: [m] });
				return [m];
			});

			watch(
				() => include.value,
				() => {
					GetReport();
				},
				{ deep: true }
			);

			const GetReport = async () => {
				report.value.loading = true;

				if (props.companyId > 0) {
					const response = await $http.get(
						"/api/v2/company/" + props.companyId + "/hierarchy?include=" + include.value
					);
					report.value.filter.keys = response.data;
				} else {
					report.value.filter.keys = props.filters.keys || [];
				}

				report.value.filter.date = JSON.parse(JSON.stringify(props.filters.date || defaultDate));
				report.value.filter.date.startDate = moment
					.utc(report.value.filter.date.startDate)
					.startOf("quarter")
					.toDate();
				report.value.filter.date.endDate = moment
					.utc(report.value.filter.date.endDate)
					.subtract(3, "months")
					.endOf("quarter")
					.toDate();

				context.emit("dates", {
					selection: "Custom",
					startDate: report.value.filter.date.startDate,
					endDate: report.value.filter.date.endDate
				});

				loadingFacade.value = true;
				await GetFacade();
				loadingFacade.value = false;

				await $financial.getReport(report.value);

				report.value.loading = false;
				hasRan.value = true;
			};

			const ToQuarter = (d) => {
				return "Q" + moment(d).utc().format("Q YYYY");
			};

			const _facade = ref({});
			const BuildFacade = (result) => {
				_facade.value = { ..._facade.value, ...result };
			};

			const GetFacade = async () => {
				const copy = { ...report.value, ...{ results: null, compare: null } };
				try {
					const response = await $http.post("api/v2/financial/report/facade", {
						report: copy
					});
					facade.value = response.data;
				} catch (err) {
					console.error(err);
				}
			};

			const SaveFacade = async () => {
				const copy = { ...report.value, ...{ results: null, compare: null } };
				try {
					await $http.post("api/v2/financial/report/facade/save", {
						report: copy,
						facade: _facade.value
					});
				} catch (err) {
					console.error(err);
				}
			};

			watch(
				() => kpi_datasets.value,
				(n, o) => {
					if (n && Object.keys(n).length >= 6) {
						BuildFacade({ samples: n });
						SaveFacade();
					}
				},
				{ deep: true }
			);

			onMounted(() => {
				GetReport();
			});

			return {
				include,
				report,
				metrics,
				loadingFacade,
				facade,
				ToQuarter
			};
		}
	};
</script>
